import { useMemo } from "react";
import { useSearchParameters } from "./use-search-parameters";
import { TConversationListFilter } from "../lib/load-conversations";
import { ConversationTab } from "../components/customer-portal/customer-portal-context";

export function useConversationListParameters(): [
  TConversationParameters,
  (updates: TSetConversationParameters) => void
] {
  const [params, setParams] = useSearchParameters<{
    filters: string;
    sorts: string;
  }>();

  const parameters = useMemo(
    () => ({
      filters: parseJSON(params[filtersKey], {
        [ConversationTab.customer]: null,
        [ConversationTab.account]: null,
      }),
      sorts: parseJSON(params[sortsKey], {
        [ConversationTab.customer]: defaultSort,
        [ConversationTab.account]: defaultSort,
      }),
    }),
    [params[filtersKey], params[sortsKey]]
  );

  const setParameters = (updates: TSetConversationParameters) => {
    const newParams: Record<string, string> = {};

    const updatedResult =
      typeof updates === "function" ? updates(parameters) : updates;

    if (updatedResult.filters) {
      newParams[filtersKey] = JSON.stringify(updatedResult.filters);
    }
    if (updatedResult.sorts) {
      newParams[sortsKey] = JSON.stringify(updatedResult.sorts);
    }

    setParams(newParams);
  };

  return [parameters, setParameters];
}

// #region Helpers

const defaultSort = "last_message_at:desc";
const filtersKey = "filters";
const sortsKey = "sorts";

const parseJSON = <T extends object>(
  jsonStr: string | null,
  defaultValue: T
): T => {
  if (!jsonStr) return defaultValue;

  try {
    return JSON.parse(jsonStr);
  } catch {
    return defaultValue;
  }
};

// #endregion

// #region Types

type TConversationParameters = {
  filters: Record<ConversationTab, TConversationListFilter | null>;
  sorts: Record<ConversationTab, string>;
};

type TSetConversationParameters =
  | Partial<TConversationParameters>
  | ((prev: TConversationParameters) => Partial<TConversationParameters>);

// #endregion
